import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = '/api/v1';

/** Lets staff-api apply the portal refresh TTL instead of the mechanic-app 7d (#2215). */
const OS_BUDDY_CLIENT_HEADER = 'X-OS-Buddy-Client';
const OS_BUDDY_CLIENT = 'portal';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
    /** Server-echoed x-request-id, quoted in 5xx toasts (#3352). */
    public requestId: string | null = null
  ) {
    super(`${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * The request id the server echoed (#3352), or null. Tests stub fetch with
 * bare objects that have no headers, so the read must not throw.
 */
function requestIdOf(response: Response): string | null {
  try {
    return response.headers.get('x-request-id');
  } catch {
    return null;
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

type RefreshResult =
  | { type: 'success' }
  | { type: 'invalid_session' }
  | { type: 'locked' }
  | { type: 'temporary_failure'; error: ApiError };

// Shared promise to deduplicate concurrent refresh calls *within one session*.
// When multiple requests get 401 at the same time, only the first
// one actually calls the refresh endpoint. The rest wait for the same promise.
// A newer session must not wait on, or inherit, a previous generation's refresh.
let refreshPromise: Promise<RefreshResult> | null = null;
let refreshEpoch: number | null = null;

function isRefreshAuthFailure(status: number): boolean {
  // Validation errors from /auth/refresh can indicate a client/server contract bug,
  // not necessarily an invalid session, so only treat auth-specific statuses as logout conditions.
  // 423 is a locked portal session (#2214): not a sign-out.
  return status === 401 || status === 403;
}

export function isSessionLockedStatus(status: number): boolean {
  return status === 423;
}

/**
 * Endpoints that must run without trying to mint a new access token.
 *
 * Logout has to work while locked (8h idle still signs out of Paya ID).
 * Unlock is the proof that clears lockedAt. Lock itself is the stamp.
 * Refresh is the thing being skipped.
 * `/auth/context` is public: the lock overlay needs it after a reload with
 * an empty query cache, and a 423 here is an endless spinner.
 */
function isAuthLifecycleEndpoint(endpoint: string): boolean {
  return (
    endpoint === '/auth/refresh' ||
    endpoint === '/auth/logout' ||
    endpoint === '/auth/logout-all' ||
    endpoint === '/auth/lock' ||
    endpoint === '/auth/unlock' ||
    endpoint === '/auth/unlock/oidc' ||
    endpoint === '/auth/oidc/handoff' ||
    endpoint === '/auth/context'
  );
}

async function refreshTokens(): Promise<RefreshResult> {
  const epoch = useAuthStore.getState().sessionEpoch;
  if (refreshPromise !== null && refreshEpoch === epoch) {
    return refreshPromise;
  }

  const thisPromise = doRefreshTokens();
  refreshPromise = thisPromise;
  refreshEpoch = epoch;
  try {
    return await thisPromise;
  } finally {
    if (refreshPromise === thisPromise) {
      refreshPromise = null;
      refreshEpoch = null;
    }
  }
}

const REFRESH_LOCK_NAME = 'staff-portal-auth-refresh';

/**
 * Run one refresh at a time across every tab of the origin (#3392). Two tabs
 * spending the same refresh token race: the server rotates for the first and
 * answers 401 to the second, which then signs every tab out. Without the Web
 * Locks API the refresh runs unserialized; the 401 recovery below still holds.
 */
async function withCrossTabRefreshLock(
  run: () => Promise<RefreshResult>,
): Promise<RefreshResult> {
  if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
    return run();
  }
  return await navigator.locks.request(REFRESH_LOCK_NAME, run);
}

async function doRefreshTokens(): Promise<RefreshResult> {
  return withCrossTabRefreshLock(async () => {
    const epoch = useAuthStore.getState().sessionEpoch;
    // A sibling tab may have rotated while this one waited for the lock:
    // its tokens are fresh, and ours were deleted server-side.
    if (useAuthStore.getState().adoptSiblingTokens()) {
      return { type: 'success' };
    }
    return refreshWithStoredToken(epoch);
  });
}

async function refreshWithStoredToken(epoch: number): Promise<RefreshResult> {
  const started = useAuthStore.getState();
  if (started.sessionEpoch !== epoch) {
    return { type: 'invalid_session' };
  }
  const { refreshToken } = started;

  if (!refreshToken) {
    useAuthStore.getState().clearAuthIfCurrent(epoch);
    return { type: 'invalid_session' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [OS_BUDDY_CLIENT_HEADER]: OS_BUDDY_CLIENT,
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (useAuthStore.getState().sessionEpoch !== epoch) {
      return { type: 'invalid_session' };
    }

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      const error = new ApiError(response.status, response.statusText, data);

      if (isSessionLockedStatus(response.status)) {
        if (useAuthStore.getState().sessionEpoch === epoch) {
          useAuthStore.getState().lockSession();
        }
        return { type: 'locked' };
      }

      if (isRefreshAuthFailure(response.status)) {
        // Our token was spent by a sibling tab (no Web Locks, or a write that
        // landed after we read): take its rotation instead of signing out.
        if (useAuthStore.getState().adoptSiblingTokens()) {
          return { type: 'success' };
        }
        useAuthStore.getState().clearAuthIfCurrent(epoch);
        return { type: 'invalid_session' };
      }

      return { type: 'temporary_failure', error };
    }

    if (useAuthStore.getState().isLocked) {
      // A refresh that was already in flight when the overlay locked must
      // not write the access token back over the drop (#2214).
      return { type: 'locked' };
    }

    const data = await response.json() as { accessToken: string; refreshToken: string };
    useAuthStore.getState().applyRotatedTokens(epoch, data.accessToken, data.refreshToken);
    if (useAuthStore.getState().sessionEpoch !== epoch) {
      return { type: 'invalid_session' };
    }
    if (useAuthStore.getState().isLocked) {
      return { type: 'locked' };
    }
    return { type: 'success' };
  } catch (error) {
    if (useAuthStore.getState().sessionEpoch !== epoch) {
      return { type: 'invalid_session' };
    }
    return {
      type: 'temporary_failure',
      error: new ApiError(
        503,
        'Service Unavailable',
        error instanceof Error
          ? { message: error.message }
          : { message: 'Unable to refresh session' }
      ),
    };
  }
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const lifecycle = isAuthLifecycleEndpoint(endpoint);
  const started = useAuthStore.getState();
  let accessToken = started.accessToken;
  const { params, ...fetchOptions } = options;

  if (!lifecycle && started.isLocked) {
    throw new ApiError(423, 'Locked', { code: 'session_locked' });
  }

  // Build URL with query params
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Set default headers
  const headers: HeadersInit = {
    ...(fetchOptions.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...fetchOptions.headers,
    [OS_BUDDY_CLIENT_HEADER]: OS_BUDDY_CLIENT,
  };

  if (!accessToken && started.refreshToken && !lifecycle) {
    const refreshResult = await refreshTokens();
    if (refreshResult.type === 'success') {
      accessToken = useAuthStore.getState().accessToken;
    } else if (refreshResult.type === 'locked') {
      throw new ApiError(423, 'Locked', { code: 'session_locked' });
    } else if (refreshResult.type === 'temporary_failure') {
      throw refreshResult.error;
    } else {
      throw new ApiError(401, 'Unauthorized');
    }
  }

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  let response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (isSessionLockedStatus(response.status) && !lifecycle) {
    if (useAuthStore.getState().sessionEpoch === started.sessionEpoch) {
      useAuthStore.getState().lockSession();
    }
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }

  // Handle 401 - try to refresh token
  if (response.status === 401 && (accessToken || started.refreshToken) && !lifecycle) {
    const refreshResult = await refreshTokens();
    if (refreshResult.type === 'success') {
      const newAccessToken = useAuthStore.getState().accessToken;
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
      response = await fetch(url, {
        ...fetchOptions,
        headers,
      });
    } else if (refreshResult.type === 'locked') {
      throw new ApiError(423, 'Locked', { code: 'session_locked' });
    } else if (refreshResult.type === 'temporary_failure') {
      throw refreshResult.error;
    }
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data, requestIdOf(response));
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text) as T;
}

async function requestBlob(endpoint: string): Promise<Blob> {
  const started = useAuthStore.getState();
  let accessToken = started.accessToken;
  const headers: HeadersInit = {
    [OS_BUDDY_CLIENT_HEADER]: OS_BUDDY_CLIENT,
  };
  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }
  if (started.isLocked) {
    throw new ApiError(423, 'Locked', { code: 'session_locked' });
  }

  if (!accessToken && started.refreshToken) {
    const refreshResult = await refreshTokens();
    if (refreshResult.type === 'success') {
      accessToken = useAuthStore.getState().accessToken;
      if (accessToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      }
    } else if (refreshResult.type === 'locked') {
      throw new ApiError(423, 'Locked', { code: 'session_locked' });
    } else if (refreshResult.type === 'temporary_failure') {
      throw refreshResult.error;
    } else {
      throw new ApiError(401, 'Unauthorized');
    }
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'GET', headers });
  if (isSessionLockedStatus(response.status)) {
    if (useAuthStore.getState().sessionEpoch === started.sessionEpoch) {
      useAuthStore.getState().lockSession();
    }
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }
  if (response.status === 401 && (accessToken || started.refreshToken)) {
    const refreshResult = await refreshTokens();
    if (refreshResult.type === 'success') {
      const newAccessToken = useAuthStore.getState().accessToken;
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
      response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'GET', headers });
    } else if (refreshResult.type === 'locked') {
      throw new ApiError(423, 'Locked', { code: 'session_locked' });
    } else if (refreshResult.type === 'temporary_failure') {
      throw refreshResult.error;
    }
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data, requestIdOf(response));
  }
  return response.blob();
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  put: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data ?? {}),
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data ?? {}),
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  getBlob: (endpoint: string) => requestBlob(endpoint),
};
